import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaEye, FaRulerCombined, FaTimes, FaCheckCircle } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import api from "../../api/axios";
import "./PropertyDetailsPage.css";
import Loading from "../../components/Loading/Loading";

const UNITS_PER_PAGE = 10;

const PropertyDetailsPage = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [unitsPage, setUnitsPage] = useState(1);
  const [unitsLastPage, setUnitsLastPage] = useState(1);
  const [unitsTotal, setUnitsTotal] = useState(0);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); 
  
  // Image Slider & Zoom State
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);

  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    if (token && user && user !== "null" && user !== "undefined") {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      try {
        const queryId = id || 1;
        const response = await api.get(`/api/get-properties/${queryId}`);
        setProperty(response.data.data || response.data);
      } catch (err) {
        console.error("Error fetching property:", err);
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPropertyDetails();
  }, [id]);

  const loadUnits = useCallback(
    async (page = 1) => {
      const queryId = id || property?.id;
      if (!queryId) return;
      setUnitsLoading(true);
      try {
        const response = await api.get(`/api/get-units`, {
          params: { property_id: queryId, page, per_page: UNITS_PER_PAGE },
        });
        const data = response.data;
        setUnits(Array.isArray(data?.unit) ? data.unit : []);
        setUnitsPage(data?.current_page ?? 1);
        setUnitsLastPage(data?.last_page ?? 1);
        setUnitsTotal(data?.total ?? 0);
      } catch (err) {
        console.error("Error fetching units:", err);
        setUnits([]);
      } finally {
        setUnitsLoading(false);
      }
    },
    [id, property?.id]
  );

  useEffect(() => {
    if (property?.id) loadUnits(1);
  }, [property?.id, loadUnits]);

  const handleViewUnit = (unit) => {
    setSelectedUnit(unit);
    setShowCheckInModal(true);
  };

  const handleCheckIn = (unit) => {
    if (!isLoggedIn) {
      navigate("/login", {
        state: {
          message: "Please login to book this unit",
          redirectUrl: window.location.pathname,
        },
      });
      return;
    }
    setSelectedUnit(unit);
    setShowPaymentModal(true);
  };

  const handleCheckInFromModal = () => {
    setShowCheckInModal(false);
    setShowPaymentModal(true);
  };

  const handlePayment = (method) => {
    setPaymentSuccess(true);
    setTimeout(() => {
      setShowPaymentModal(false);
      setPaymentSuccess(false);
      setSelectedUnit(null);
    }, 2000);
  };

  const getUnitArea = (unit) => {
    if (unit.measurement && unit.measurement.length > 0) {
      return parseFloat(unit.measurement[0].area_sq_ft).toLocaleString();
    }
    return "N/A";
  };

  const getUnitMeasureType = (unit) => {
    if (unit.measurement && unit.measurement.length > 0) {
      return unit.measurement[0].unit_of_measure;
    }
    return "sq-ft";
  };

  const formatPrice = (unit) => {
    if (unit.purpose === "Rent") {
      return unit.rent_amount
        ? `৳ ${parseFloat(unit.rent_amount).toLocaleString()}/month`
        : "Rent TBD";
    } else {
      return unit.price
        ? `৳ ${parseFloat(unit.price).toLocaleString()}`
        : "Price on request";
    }
  };

  const getImageUrl = (photoObj) => {
    if (photoObj && photoObj.file_path) {
      return `${api.defaults.baseURL}/${photoObj.file_path}`;
    }
    return "https://via.placeholder.com/600x400?text=No+Image";
  };

  const nextImage = () => {
    if (property?.photos?.length > 0) {
      setCurrentPhotoIndex((prev) => (prev + 1) % property.photos.length);
    }
  };

  const prevImage = () => {
    if (property?.photos?.length > 0) {
      setCurrentPhotoIndex((prev) => (prev - 1 + property.photos.length) % property.photos.length);
    }
  };

  if (loading) return <Loading></Loading>;
  if (error) return <div className="container error">Error: {error}</div>;
  if (!property) return <div className="container error">Property not found</div>;

  return (
    <div className="container">
      <div className="propertyCard">
        <div className="cardHeader">
          <h2 className="propertyTitle">{property.name}</h2>
          <div className="badgeContainer">
            <span className="badge">{property.type?.name || "Property"}</span>
            <span
              className={`statusBadge ${
                property.status?.status_type?.toLowerCase() === "available"
                  ? "available"
                  : "booked"
              }`}
            >
              {property.status?.status_type || "Unknown"}
            </span>
          </div>
        </div>

        <div className="propertyContent">
          <div className="propertyInfo">
            <div className="infoRow">
              <strong>Location :</strong>
              <span>
                {property.location?.area}, Road {property.location?.road_number}
                , Block {property.location?.block}, House{" "}
                {property.location?.house_number}
              </span>
              <span className="locationDetail">
                {property.location?.city}, {property.location?.state},{" "}
                {property.location?.postal_code}
              </span>
            </div>
            <div className="infoGrid">
              <div className="infoItem">
                <strong>Total Floors :</strong>
                <span>{property.total_floors || "N/A"}</span>
              </div>
              <div className="infoItem">
                <strong>Total Units :</strong>
                <span>{property.total_units ?? 0}</span>
              </div>
              <div className="infoItem">
                <strong>Total Land Area :</strong>
                <span>
                  {property.area_sq_ft
                    ? parseFloat(property.area_sq_ft).toLocaleString()
                    : "N/A"}{" "}
                  katha
                </span>
              </div>
              <div className="infoItem">
                <strong>Total Flats (per floor) :</strong>
                <span>{8}</span>
              </div>
              <div className="infoItem">
                <strong>Total Flats (From floor 2 to 15) :</strong>
                <span>{property.total_units ?? 0}</span>
              </div>
              <div className="infoItem">
                <strong>Shops (Ground floor) :</strong>
                <span>{49}</span>
              </div>
              <div className="infoItem">
                <strong>Shops (1st floor) :</strong>
                <span>{53}</span>
              </div>
              <div className="infoItem">
                <strong>Class :</strong>
                <span>{property.type?.class || "N/A"}</span>
              </div>
            </div>
            <div className="description">
              <strong>Description :</strong>
              <p>{property.description}</p>
            </div>
          </div>

          <div className="propertyPhotos">
            {property.photos && property.photos.length > 0 ? (
              <div className="sliderContainer">
                <img
                  src={getImageUrl(property.photos[currentPhotoIndex])}
                  alt={`Property Photo ${currentPhotoIndex + 1}`}
                  className="fullBuildingImage zoomable"
                  onClick={() => setIsZoomModalOpen(true)}
                  onError={(e) =>
                    (e.target.src = "https://via.placeholder.com/600x400?text=Error")
                  }
                />
                
                {property.photos.length > 1 && (
                  <>
                    <button className="sliderBtn prevBtn" onClick={prevImage}>
                      &#10094;
                    </button>
                    <button className="sliderBtn nextBtn" onClick={nextImage}>
                      &#10095;
                    </button>
                    
                    <div className="sliderPagination">
                      {property.photos.map((_, idx) => (
                        <span
                          key={idx}
                          className={`dot ${idx === currentPhotoIndex ? "active" : ""}`}
                          onClick={() => setCurrentPhotoIndex(idx)}
                        ></span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="noPhotos">No photos available</div>
            )}
          </div>
        </div>
      </div>

      <div className="contactCard">
        <div className="contactSection">
          <h4 className="contactTitle">Owner Information</h4>
          <div className="contactInfo">
            {property.owner ? (
              <>
                <strong>{property.owner.name}</strong>
                {/* Visitors only get the name; email and phone come with a signed-in session. */}
                {property.owner.email && <div>Email: {property.owner.email}</div>}
                {property.owner.mobile_number && (
                  <div>Phone: {property.owner.mobile_number}</div>
                )}
              </>
            ) : (
              <div>ID: {property.owner_id} (Contact Admin for Details)</div>
            )}
          </div>
        </div>
        <div className="contactSection">
          <h4 className="contactTitle">Manager Information</h4>
          <div className="contactInfo">
            {property.manager ? (
              <>
                <strong>{property.manager.name}</strong>
                {/* Visitors only get the name; email and phone come with a signed-in session. */}
                {property.manager.email && <div>Email: {property.manager.email}</div>}
                {property.manager.mobile_number && (
                  <div>Phone: {property.manager.mobile_number}</div>
                )}
              </>
            ) : (
              <div>ID: {property.manager_id} (Contact Admin for Details)</div>
            )}
          </div>
        </div>
      </div>

      <div className="unitsCard">
        <h3 className="unitsTitle">
          Units ({property.units_count ?? property.total_units ?? 0})
        </h3>
        {unitsLoading ? (
          <p className="text-muted" style={{ padding: "1rem" }}>
            Loading units...
          </p>
        ) : unitsTotal === 0 ? (
          <p className="text-muted" style={{ padding: "1rem" }}>
            No units available.
          </p>
        ) : (
          <>
            <div className="tableContainer">
              <table className="table">
                <thead>
                  <tr className="tableHeaderRow">
                    <th className="th">Unit #</th>
                    <th className="th">Purpose</th>
                    <th className="th">Floor</th>
                    <th className="th">Status</th>
                    <th className="th">Price</th>
                    <th className="th">Area</th>
                    <th className="th">Facilities</th>
                    <th className="th">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((unit) => (
                    <tr key={unit.id} className="tableRow">
                      <td className="td">
                        <strong>{unit.unit_number}</strong>
                      </td>
                      <td className="td">
                        <span className={`purposeBadge ${unit.purpose?.toLowerCase()}`}>
                          {unit.purpose}
                        </span>
                      </td>
                      <td className="td">{unit.floor || "N/A"}</td>
                      <td className="td">
                        <span
                          className={`statusBadge ${
                            unit.status?.status_type === "available"
                              ? "available"
                              : "booked"
                          }`}
                        >
                          {unit.status?.status_type || "Unknown"}
                        </span>
                      </td>
                      <td className="td">
                        <div className="priceCell">
                          <strong>{formatPrice(unit)}</strong>
                        </div>
                      </td>
                      <td className="td">
                        <div className="iconWithText">
                          <FaRulerCombined className="tableIcon" />
                          {getUnitArea(unit)} {getUnitMeasureType(unit)}
                        </div>
                      </td>
                      <td className="td">
                        <div className="facilitiesCell">
                          {unit.facilities &&
                            unit.facilities.slice(0, 2).map((facility, idx) => (
                              <span key={idx} className="facilityTag">
                                {facility}
                              </span>
                            ))}
                          {unit.facilities && unit.facilities.length > 2 && (
                            <span className="facilityTag">
                              +{unit.facilities.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="td">
                        <div className="actionButtons">
                          <button
                            onClick={() => handleCheckIn(unit)}
                            className={`checkInButtonTable ${
                              unit.status?.status_type === "available"
                                ? ""
                                : "checkInButtonDisabled"
                            }`}
                            disabled={unit.status?.status_type !== "available"}
                          >
                            Book Now
                          </button>
                          <button
                            onClick={() => handleViewUnit(unit)}
                            className="viewButton"
                          >
                            <FaEye className="buttonIcon" />
                            Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {unitsTotal > 0 && (
              <div
                className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                style={{ padding: "0.75rem 1rem", borderTop: "1px solid #eee" }}
              >
                <span className="text-muted small">
                  Showing {unitsPage === 1 ? 1 : (unitsPage - 1) * UNITS_PER_PAGE + 1}–
                  {Math.min(unitsPage * UNITS_PER_PAGE, unitsTotal)} of {unitsTotal} units
                </span>
                <div className="d-flex gap-1 align-items-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={unitsPage <= 1 || unitsLoading}
                    onClick={() => loadUnits(unitsPage - 1)}
                  >
                    Previous
                  </button>
                  <span className="px-2 small">
                    Page {unitsPage} of {unitsLastPage}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={unitsPage >= unitsLastPage || unitsLoading}
                    onClick={() => loadUnits(unitsPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showCheckInModal && selectedUnit && (
        <div className="modalOverlay" onClick={() => setShowCheckInModal(false)}>
          <div className="view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modalHeader">
              <h3 className="modalTitle">Unit Details - {selectedUnit.unit_number}</h3>
              <button onClick={() => setShowCheckInModal(false)} className="closeButton">
                <FaTimes />
              </button>
            </div>
            <div className="modalBody">
              <img
                src={
                  selectedUnit.photos && selectedUnit.photos.length > 0
                    ? getImageUrl(selectedUnit.photos[0])
                    : "https://via.placeholder.com/600x400"
                }
                alt={selectedUnit.unit_number}
                className="modalImage"
              />
              <div className="modalInfo">
                <div className="modalDetailGrid">
                  <div className="modalDetail">
                    <strong>Purpose:</strong> {selectedUnit.purpose}
                  </div>
                  <div className="modalDetail">
                    <strong>Floor:</strong> {selectedUnit.floor || "N/A"}
                  </div>
                  <div className="modalDetail">
                    <strong>Status:</strong>
                    <span
                      className={`modalStatusBadge ${
                        selectedUnit.status?.status_type === "available"
                          ? "available"
                          : "booked"
                      }`}
                    >
                      {selectedUnit.status?.status_type}
                    </span>
                  </div>
                  <div className="modalDetail">
                    <strong>Area:</strong> {getUnitArea(selectedUnit)}{" "}
                    {getUnitMeasureType(selectedUnit)}
                  </div>
                  <div className="modalDetail">
                    <strong>Price:</strong> {formatPrice(selectedUnit)}
                  </div>
                </div>
              </div>
            </div>
            <div className="modalFooter">
              <button
                onClick={handleCheckInFromModal}
                className="checkInButton"
                disabled={selectedUnit.status?.status_type !== "available"}
              >
                {selectedUnit.status?.status_type === "available"
                  ? "Proceed to Check-in"
                  : "Unit Not Available"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && (
        <div className="modalOverlay" onClick={() => setShowPaymentModal(false)}>
          <div className="paymentModal" onClick={(e) => e.stopPropagation()}>
            <div className="modalHeader">
              <h3 className="modalTitle">Select Payment Method</h3>
              <button onClick={() => setShowPaymentModal(false)} className="closeButton">
                <FaTimes />
              </button>
            </div>
            <div className="paymentBody">
              {paymentSuccess ? (
                <div className="successMessage">
                  <FaCheckCircle className="successIcon" />
                  <h4>Payment Successful!</h4>
                  <p>Check-in completed for {selectedUnit?.unit_number}</p>
                </div>
              ) : (
                <>
                  <p className="paymentAmount">
                    Amount to pay: <strong>{formatPrice(selectedUnit)}</strong>
                  </p>
                  <button onClick={() => handlePayment("Bkash")} className="paymentButton">
                    <span>Pay with Bkash</span>
                  </button>
                  <button onClick={() => handlePayment("Nagad")} className="paymentButton">
                    <span>Pay with Nagad</span>
                  </button>
                  <button onClick={() => handlePayment("Cash")} className="paymentButton">
                    <span>Pay with Cash</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ----------- Full Screen Image Zoom Slider Modal ----------- */}
      {isZoomModalOpen && property?.photos?.length > 0 && (
        <div className="zoomModalOverlay" onClick={() => setIsZoomModalOpen(false)}>
          {/* MdClose icon — clearly visible on dark overlay */}
          <button className="zoomCloseBtn" onClick={() => setIsZoomModalOpen(false)}>
            <MdClose />
          </button>
          <div className="zoomModalContent" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageUrl(property.photos[currentPhotoIndex])}
              alt={`Zoomed Property Photo ${currentPhotoIndex + 1}`}
              className="zoomedImage"
            />
            {property.photos.length > 1 && (
              <>
                <button className="zoomSliderBtn zoomPrevBtn" onClick={(e) => { e.stopPropagation(); prevImage(); }}>
                  &#10094;
                </button>
                <button className="zoomSliderBtn zoomNextBtn" onClick={(e) => { e.stopPropagation(); nextImage(); }}>
                  &#10095;
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default PropertyDetailsPage;